import React, { useCallback, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Tooltip,
  Typography,
  useTheme,
  IconButton,
} from '@mui/material'
import {
  Edit,
  Delete,
  Email,
  Phone,
  Person,
  OpenInNew,
} from '@mui/icons-material'
import { OtwAgGridReact as AgGridReact } from '@/components/AgGrid/OtwAgGridReact'
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  ValueGetterParams,
} from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { TransporterWithPackages } from '@otw/models'
import { formatTransporterName } from '../common'
import DeleteTransporterDialog from '../dialogs/DeleteTransporterDialog'
import { useRepairShopLocations } from '@/hooks/inventory/useRepairShopLocations'
import { MobileRecordAction, MobileRecordCard } from 'components/MobileRecordCard'
import { MobileRecordList } from 'components/MobileRecordList'
import type { StatusTone } from 'core/theme.tokens'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { logisticsUserStatusTone } from '../../utils/mobileStatusTones'

export type LogisticsUserGridRow = TransporterWithPackages & {
  /** True when this user has a `transporters/{id}` profile */
  hasTransportAccess: boolean
  /** True when this user has a `repair_users/{id}` profile */
  hasRepairAccess: boolean
  /** Linked repair shop id when `hasRepairAccess` (dual or repair-only) */
  repair_shop_id?: string
  repair_shop_ids?: string[]
  shop_id?: string
}

interface UsersGridProps {
  transporters: LogisticsUserGridRow[]
  onEditTransporter: (transporter: TransporterWithPackages) => void
  onDeleteTransporter: (userId: string) => void
  onEditUser?: (transporter: TransporterWithPackages) => void
  onViewTransporterLicense: (transporter: TransporterWithPackages) => Promise<void>
}

const UsersGrid: React.FC<UsersGridProps> = ({
  transporters,
  onEditTransporter,
  onDeleteTransporter,
  onEditUser,
  onViewTransporterLicense,
}) => {
  const theme = useTheme()
  const isMobile = useIsMobile()
  const gridApiRef = useRef<GridApi | null>(null)
  const { repairShopLocations } = useRepairShopLocations()

  const repairShopNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const loc of repairShopLocations ?? []) {
      if (!loc.id) continue
      const extended = loc as { name?: string; company_name?: string }
      const label =
        extended.name?.trim() ||
        loc.contact_name?.trim() ||
        extended.company_name?.trim() ||
        [loc.city, loc.state].filter(Boolean).join(', ') ||
        loc.id
      m.set(loc.id, label)
    }
    return m
  }, [repairShopLocations])

  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [transporterToDelete, setTransporterToDelete] = useState<LogisticsUserGridRow | null>(null)
  
  const onGridReady = useCallback((params: GridReadyEvent) => {
    gridApiRef.current = params.api
  }, [])

  // Handle delete confirmation
  const handleDeleteClick = useCallback((transporter: LogisticsUserGridRow) => {
    setTransporterToDelete(transporter)
    setDeleteDialogOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(() => {
    if (transporterToDelete) {
      onDeleteTransporter(transporterToDelete.id)
      setDeleteDialogOpen(false)
      setTransporterToDelete(null)
    }
  }, [transporterToDelete, onDeleteTransporter])

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false)
    setTransporterToDelete(null)
  }, [])

  // Transporter name renderer
  const TransporterNameRenderer = (props: ICellRendererParams) => {
    const transporter = props.data as TransporterWithPackages
    const displayName = formatTransporterName(transporter)
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Person fontSize="small" sx={{ color: 'primary.main' }} />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {displayName}
        </Typography>
      </Box>
    )
  }

  // Email renderer
  const EmailRenderer = (props: ICellRendererParams) => {
    const transporter = props.data as TransporterWithPackages
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Email fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography variant="body2">
          {transporter.email}
        </Typography>
      </Box>
    )
  }

  // Phone renderer
  const PhoneRenderer = (props: ICellRendererParams) => {
    const transporter = props.data as TransporterWithPackages
    
    if (!transporter.phone) {
      return (
        <Typography variant="body2" color="text.secondary">
          No phone
        </Typography>
      )
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Phone fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography variant="body2">
          {transporter.phone}
        </Typography>
      </Box>
    )
  }


  const asRow = (data: unknown) => data as LogisticsUserGridRow

  /** All repair shop ids for a row (multi-shop aware), matching Firestore `shop_ids` / legacy `shop_id`. */
  const getRepairShopIdsForRow = useCallback((row: LogisticsUserGridRow): string[] => {
    if (row.repair_shop_ids && row.repair_shop_ids.length > 0) {
      return row.repair_shop_ids
    }
    const single = row.repair_shop_id || row.shop_id
    return single ? [single] : []
  }, [])

  const TransportAccessRenderer = (props: ICellRendererParams) => {
    const row = asRow(props.data)
    return (
      <Chip
        label={row.hasTransportAccess ? 'Yes' : 'No'}
        color={row.hasTransportAccess ? 'primary' : 'default'}
        size="small"
        variant={row.hasTransportAccess ? 'filled' : 'outlined'}
        sx={{ fontSize: '0.7rem', fontWeight: 600, width: 'fit-content' }}
      />
    )
  }

  const TransportRoleRenderer = (props: ICellRendererParams) => {
    const row = asRow(props.data)
    if (!row.hasTransportAccess) {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      )
    }
    const role = (row.transporter_role as string) || 'driver'
    return (
      <Chip
        label={role.charAt(0).toUpperCase() + role.slice(1)}
        color={
          role === 'owner' ? 'primary' : role === 'admin' ? 'secondary' : 'default'
        }
        size="small"
        sx={{ fontSize: '0.7rem', fontWeight: 600, width: 'fit-content' }}
      />
    )
  }

  const RepairAccessRenderer = (props: ICellRendererParams) => {
    const row = asRow(props.data)
    return (
      <Chip
        label={row.hasRepairAccess ? 'Yes' : 'No'}
        color={row.hasRepairAccess ? 'warning' : 'default'}
        size="small"
        variant={row.hasRepairAccess ? 'filled' : 'outlined'}
        sx={{ fontSize: '0.7rem', fontWeight: 600, width: 'fit-content' }}
      />
    )
  }

  const repairShopDisplayName = useCallback(
    (shopId: string | undefined) => {
      if (!shopId) return ''
      return repairShopNameById.get(shopId) ?? shopId
    },
    [repairShopNameById],
  )

  const RepairShopRenderer = (props: ICellRendererParams) => {
    const row = asRow(props.data)
    const ids = getRepairShopIdsForRow(row)
    if (!row.hasRepairAccess || ids.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      )
    }
    const labels = ids.map((id) => repairShopDisplayName(id)).filter(Boolean)
    const text = labels.join(', ')
    return (
      <Typography variant="body2" title={text}>
        {text}
      </Typography>
    )
  }

  // Elevated renderer (transport drivers only)
  const ElevatedRenderer = (props: ICellRendererParams) => {
    const row = asRow(props.data)
    if (!row.hasTransportAccess) {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      )
    }
    const role = (row.transporter_role as string) || 'driver'
    if (role !== 'driver') {
      return (
        <Typography variant="body2" color="text.secondary">
          N/A
        </Typography>
      )
    }
    const isElevated = row.elevated || false
    return (
      <Chip
        label={isElevated ? 'Yes' : 'No'}
        color={isElevated ? 'success' : 'default'}
        size="small"
        sx={{
          fontSize: '0.7rem',
          fontWeight: 600,
          width: 'fit-content',
        }}
      />
    )
  }

  // Created date renderer
  const CreatedDateRenderer = (props: ICellRendererParams) => {
    const transporter = props.data as TransporterWithPackages
    
    if (!transporter.created_at) {
      return (
        <Typography variant="body2" color="text.secondary">
          N/A
        </Typography>
      )
    }

    const createdDate = transporter.created_at.toDate ? transporter.created_at.toDate() : new Date(transporter.created_at.toMillis())
    const formattedDate = createdDate.toLocaleDateString()
    
    return (
      <Typography variant="body2">
        {formattedDate}
      </Typography>
    )
  }

  // Actions renderer
  const ActionsRenderer = (props: ICellRendererParams) => {
    const row = asRow(props.data)
    const canShowLicense = row.hasTransportAccess && !!row.driver_license_front_storage_path

    return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        {onEditUser && (
          <Tooltip title="Edit user">
            <IconButton
              size="small"
              onClick={() => onEditUser(row)}
              sx={{ color: 'primary.main' }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip
          title={
            !row.hasTransportAccess
              ? 'No transport profile — license is only for transport users'
              : row.driver_license_front_storage_path
                ? 'Open license photo'
                : 'No license photo'
          }
        >
          <span>
            <IconButton
              size="small"
              onClick={() => onViewTransporterLicense(row)}
              disabled={!canShowLicense}
              sx={{ color: 'info.main' }}
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete user (removes all logistics access and account)">
          <IconButton
            size="small"
            onClick={() => handleDeleteClick(row)}
            sx={{ color: 'error.main' }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    )
  }

  const columns: ColDef[] = useMemo(() => [
    {
      field: 'actions',
      headerName: 'Actions',
      width: onEditUser ? 200 : 160,
      minWidth: onEditUser ? 200 : 160,
      cellRenderer: ActionsRenderer,
      sortable: false,
      filter: false,
      pinned: 'left',
      lockPinned: true,
      lockPosition: true,
      suppressNavigable: true,
    },
    {
      field: 'transporterName',
      headerName: 'User',
      width: 300,
      cellRenderer: TransporterNameRenderer,
      sortable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      valueGetter: (params: ValueGetterParams) => {
        const transporter = params.data as TransporterWithPackages
        return formatTransporterName(transporter)
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 300,
      cellRenderer: EmailRenderer,
      sortable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 200,
      cellRenderer: PhoneRenderer,
      sortable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
    },
    {
      field: 'transportAccess',
      headerName: 'Transport',
      width: 130,
      cellRenderer: TransportAccessRenderer,
      sortable: true,
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      valueGetter: (params: ValueGetterParams) => (asRow(params.data).hasTransportAccess ? 'Yes' : 'No'),
    },
    {
      field: 'transportRole',
      headerName: 'Transport role',
      width: 170,
      cellRenderer: TransportRoleRenderer,
      sortable: true,
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      valueGetter: (params: ValueGetterParams) => {
        const row = asRow(params.data)
        if (!row.hasTransportAccess) return '—'
        return (row.transporter_role as string) || 'driver'
      },
    },
    {
      field: 'repairAccess',
      headerName: 'Repair',
      width: 118,
      cellRenderer: RepairAccessRenderer,
      sortable: true,
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      valueGetter: (params: ValueGetterParams) => (asRow(params.data).hasRepairAccess ? 'Yes' : 'No'),
    },
    {
      field: 'repair_shop_id',
      headerName: 'Repair shop',
      width: 220,
      minWidth: 180,
      cellRenderer: RepairShopRenderer,
      sortable: true,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      valueGetter: (params: ValueGetterParams) => {
        const row = asRow(params.data)
        if (!row.hasRepairAccess) return ''
        const ids = getRepairShopIdsForRow(row)
        if (ids.length === 0) return ''
        return ids.map((id) => repairShopDisplayName(id)).join(', ')
      },
    },
    {
      field: 'elevated',
      headerName: 'Elevated',
      width: 150,
      cellRenderer: ElevatedRenderer,
      sortable: true,
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      valueGetter: (params: ValueGetterParams) => {
        const row = asRow(params.data)
        if (!row.hasTransportAccess || (row.transporter_role as string) !== 'driver') return false
        return row.elevated || false
      },
    },
    // {
    //   field: 'packageCount',
    //   headerName: 'Packages',
    //   width: 120,
    //   cellRenderer: PackagesCountRenderer,
    //   sortable: true,
    //   filter: 'agNumberColumnFilter',
    //   floatingFilter: true,
    //   valueGetter: (params: ValueGetterParams) => {
    //     const transporter = params.data as TransporterWithPackages
    //     return transporter.packages?.length || 0
    //   },
    // },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      cellRenderer: CreatedDateRenderer,
      sortable: true,
      filter: 'agDateColumnFilter',
      floatingFilter: true,
      valueGetter: (params: ValueGetterParams) => {
        const transporter = params.data as TransporterWithPackages
        if (!transporter.created_at) return null
        return transporter.created_at.toDate ? transporter.created_at.toDate() : new Date(transporter.created_at.toMillis())
      },
    },
  ], [onEditUser, onViewTransporterLicense, handleDeleteClick, repairShopDisplayName, getRepairShopIdsForRow])

  const defaultColDef = {
    sortable: true,
    resizable: true,
  }

  const memoizedRowData = useMemo(() => {
    // Filter out any invalid transporters and ensure all required fields exist
    const validTransporters = transporters.filter(transporter => 
      transporter && 
      transporter.id && 
      transporter.email
    )
    
    return validTransporters
  }, [transporters])

  const formatCreatedDate = (row: LogisticsUserGridRow) => {
    if (!row.created_at) return '—'
    const createdDate = row.created_at.toDate
      ? row.created_at.toDate()
      : new Date(row.created_at.toMillis())
    return createdDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const accessLaneChips = (row: LogisticsUserGridRow): { label: string; tone: StatusTone }[] => {
    const lanes: { label: string; tone: StatusTone }[] = []
    if (row.hasTransportAccess) {
      const role = (row.transporter_role as string) || 'driver'
      lanes.push({
        label: `Transport · ${role.charAt(0).toUpperCase()}${role.slice(1)}`,
        tone: 'info',
      })
    }
    if (row.hasRepairAccess) {
      const shopIds = getRepairShopIdsForRow(row)
      const shopLabel =
        shopIds.map(id => repairShopDisplayName(id)).filter(Boolean).join(', ') || 'Repair'
      lanes.push({ label: `Repair · ${shopLabel}`, tone: 'purple' })
    }
    return lanes
  }

  if (isMobile) {
    return (
      <>
        <Box sx={{ p: 1.5 }}>
          <MobileRecordList isEmpty={memoizedRowData.length === 0} empty="No users found.">
            {memoizedRowData.map(row => {
              const canShowLicense = row.hasTransportAccess && !!row.driver_license_front_storage_path
              return (
                <MobileRecordCard
                  key={row.id}
                  title={formatTransporterName(row)}
                  status={{
                    tone: logisticsUserStatusTone(row.status),
                    label: row.status || 'ACTIVE',
                  }}
                  chips={accessLaneChips(row)}
                  meta={row.email}
                  detail={`Added ${formatCreatedDate(row)}`}
                  trailing={
                    <Box sx={{ display: 'flex' }}>
                      {onEditUser && (
                        <MobileRecordAction label="Edit user" onClick={() => onEditUser(row)}>
                          <Edit fontSize="small" />
                        </MobileRecordAction>
                      )}
                      <MobileRecordAction
                        label="Open license photo"
                        onClick={() => onViewTransporterLicense(row)}
                        disabled={!canShowLicense}
                      >
                        <OpenInNew fontSize="small" />
                      </MobileRecordAction>
                      <MobileRecordAction
                        label="Delete user"
                        onClick={() => handleDeleteClick(row)}
                      >
                        <Delete fontSize="small" sx={{ color: 'error.main' }} />
                      </MobileRecordAction>
                    </Box>
                  }
                />
              )
            })}
          </MobileRecordList>
        </Box>
        <DeleteTransporterDialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          transporter={transporterToDelete}
        />
      </>
    )
  }

  return (
    <>
      <Box
        sx={{
          height: 'calc(100vh - 400px)',
          width: '100%',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          className={theme.palette.mode === 'dark' ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'}
          style={{ height: '100%', width: '100%', flex: 1 }}
        >
          <AgGridReact
            rowData={memoizedRowData}
            columnDefs={columns}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            pagination={true}
            paginationPageSize={20}
            rowSelection='multiple'
            enableCellTextSelection={true}
            suppressRowClickSelection={true}
            animateRows={false}
            suppressScrollOnNewData={true}
            rowBuffer={50}
            getRowId={(params) => params.data.id}
            overlayNoRowsTemplate='No users found'
            suppressRowTransform={true}
            suppressColumnVirtualisation={false}
            suppressRowVirtualisation={false}
          />
        </div>
      </Box>
      
      <DeleteTransporterDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        transporter={transporterToDelete}
      />
    </>
  )
}

export default UsersGrid
